/* Contractor Portal — sign-in. */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Truck, LogIn, Radar, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { useContractorAuth } from "@/context/ContractorAuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[#2C313A] text-white mono px-3 py-3 text-sm focus:outline-none focus:border-[#38BDF8] rounded-none";

export default function ContractorLogin() {
  const { login } = useContractorAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await login(email.trim(), password);
      nav("/contractor/suppliers", { replace: true });
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || "Sign-in failed";
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="contractor-theme min-h-screen bg-[#0B0D10] text-white grid lg:grid-cols-[1.1fr_1fr]" data-testid="contractor-login">
      <div className="p-8 lg:p-16 flex flex-col justify-center max-w-xl w-full mx-auto">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-11 h-11 bg-[#38BDF8] flex items-center justify-center shadow-xl shadow-sky-900/40"><Truck className="w-5 h-5 text-[#0B0D10]" /></div>
          <div>
            <div className="text-[10px] tracking-[0.28em] text-[#8B909A]">ARMORBATCH GLOBAL</div>
            <div className="heading font-black text-lg tracking-wide">CONTRACTOR <span className="text-[#38BDF8]">PORTAL</span></div>
          </div>
        </div>
        <h1 className="heading text-4xl sm:text-5xl font-black leading-[0.95] mb-3">Your trucks.<br /><span className="text-[#38BDF8]">Every plant. Live.</span></h1>
        <p className="text-[#A1A6B0] mb-10 max-w-md text-sm sm:text-base">One private map of your fleet — which plant each unit is loading at, the site it's heading to, and its real-time ETA.</p>

        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="ctr-login-form">
          <div>
            <label className="block text-[10px] tracking-widest text-[#8B909A] uppercase mb-1">Email</label>
            <input data-testid="ctr-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="username" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest text-[#8B909A] uppercase mb-1">Password</label>
            <input data-testid="ctr-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="current-password" />
          </div>
          {err && <div data-testid="ctr-login-error" className="text-sm text-red-300 border border-red-900 bg-red-950/30 p-3">{err}</div>}
          <button data-testid="ctr-login-submit" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] uppercase tracking-widest text-sm font-black py-3 transition-colors disabled:opacity-50">
            <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-10 text-xs text-[#8B909A] leading-relaxed">
          Logins are issued by the concrete plant you haul for. Lost your details? Ask the plant's dispatcher to reset your password.
          <div className="mt-3"><Link to="/login" data-testid="ctr-login-to-staff" className="text-[#38BDF8] hover:underline">Plant staff sign-in →</Link></div>
        </div>
      </div>

      <div className="hidden lg:flex relative border-l border-[#22262E] items-end p-16 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=1600&q=80')" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-[#0B0D10]/70 to-transparent" />
        <div className="relative z-10 space-y-5 max-w-md">
          {[
            [Radar, "Fleet-only visibility", "You see your registered trucks and nothing else — no plant pricing, no other contractors."],
            [MapPin, "Plant · Site · Status", "Know instantly where every unit is: loading bay, on the road, on site or returning."],
            [Clock, "Real-time ETA", "Google-routed arrival times refresh every 15 seconds while the truck moves."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="flex gap-4">
              <div className="w-9 h-9 shrink-0 bg-[#12151A] border border-[#22262E] flex items-center justify-center"><Icon className="w-4 h-4 text-[#38BDF8]" /></div>
              <div><div className="font-bold text-sm">{t}</div><div className="text-xs text-[#A1A6B0]">{d}</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
